import type { GameEvent } from '@/types';

function formatTime(createdAt: string): string {
  const isoStr = createdAt.endsWith('Z') ? createdAt : createdAt + 'Z';
  const date = new Date(isoStr);
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function classifyEvent(event: GameEvent) {
  const t = event.event_type;
  return {
    isFight: t === 'fight',
    isWaifu: t === 'waifu' || t === 'waifu_spawn',
    isQuarrel: t === 'quarrel',
    isPvP: t === 'pvp',
    isFriendlyMeet: t === 'friendly_meet',
    isSystemEvent: t === 'exhaustion' || t === 'party_wipe',
    isLocationEvent: t === 'location_event',
    isDungeon: t === 'dungeon',
    isDungeonBoss: t === 'dungeon_boss',
  };
}

function getEntryClass(c: ReturnType<typeof classifyEvent>): string {
  if (c.isFight) return 'fight-entry';
  if (c.isDungeon) return 'dungeon-entry';
  if (c.isDungeonBoss) return 'dungeon-boss-entry';
  if (c.isWaifu) return 'waifu-entry';
  if (c.isQuarrel) return 'quarrel-entry';
  if (c.isPvP) return 'pvp-entry';
  if (c.isFriendlyMeet) return 'friendly-entry';
  if (c.isSystemEvent) return 'system-entry';
  if (c.isLocationEvent) return 'location-event-entry';
  return '';
}

function getResultText(
  event: GameEvent,
  success: boolean,
  c: ReturnType<typeof classifyEvent>,
): string {
  if ((c.isFight || c.isPvP || c.isDungeon || c.isDungeonBoss) && event.fight_score) {
    return `⚔️ ${event.fight_score}`;
  }
  if (c.isDungeonBoss) return success ? '👑 Босс повержен!' : '💀 Поражение';
  if (c.isDungeon) return success ? '✅ Пройдено' : '❌ Провал';
  if (c.isWaifu) return success ? '💕 Новая спутница!' : '💔 Упущена...';
  if (c.isQuarrel) return success ? '🤝 Примирение' : '💢 Обострение';
  if (c.isPvP) return success ? '🏆 Победа!' : '💀 Поражение';
  if (c.isFriendlyMeet) return '🤗 Дружба!';
  if (c.isSystemEvent)
    return event.event_type === 'exhaustion' ? '💫 Истощение' : '💀 Поражение';
  if (c.isLocationEvent) return success ? '⭐ Свершилось!' : '💨 Не судьба...';
  return success ? 'Успех' : 'Провал';
}

function RewardsBlock({
  event,
  success,
  c,
}: {
  event: GameEvent;
  success: boolean;
  c: ReturnType<typeof classifyEvent>;
}) {
  if (c.isQuarrel) {
    return (
      <div className="journal-entry-rewards">
        {success ? (
          <>
            <span className="reward-item reward-positive">💕 +10</span>
            <span className="reward-item reward-positive">🤝 +10</span>
          </>
        ) : (
          <>
            <span className="reward-item reward-negative">💔 -10</span>
            <span className="reward-item reward-negative">⚡ -30</span>
          </>
        )}
      </div>
    );
  }

  if (
    success &&
    (event.rewards_exp > 0 ||
      event.rewards_gold > 0 ||
      event.rewards_stardust > 0)
  ) {
    return (
      <div className="journal-entry-rewards">
        {event.rewards_exp > 0 && (
          <span className="reward-item">🌟 +{event.rewards_exp}</span>
        )}
        {event.rewards_gold > 0 && (
          <span className="reward-item">
            <img
              src="/data/interface/gold.webp"
              className="reward-icon"
              alt="gold"
            />{' '}
            +{event.rewards_gold}
          </span>
        )}
        {event.rewards_stardust > 0 && (
          <span className="reward-item">
            <img
              src="/data/interface/stardust.webp"
              className="reward-icon"
              alt="stardust"
            />{' '}
            +{event.rewards_stardust}
          </span>
        )}
      </div>
    );
  }

  if (c.isPvP) {
    return (
      <div className="journal-entry-rewards">
        {success ? (
          <>
            {event.rewards_gold > 0 && (
              <span className="reward-item reward-positive">
                <img
                  src="/data/interface/gold.webp"
                  className="reward-icon"
                  alt="gold"
                />{' '}
                +{event.rewards_gold}
              </span>
            )}
            {event.rewards_stardust > 0 && (
              <span className="reward-item reward-positive">
                <img
                  src="/data/interface/stardust.webp"
                  className="reward-icon"
                  alt="stardust"
                />{' '}
                +{event.rewards_stardust}
              </span>
            )}
          </>
        ) : (
          <>
            {event.rewards_gold < 0 && (
              <span className="reward-item reward-negative">
                <img
                  src="/data/interface/gold.webp"
                  className="reward-icon"
                  alt="gold"
                />{' '}
                {event.rewards_gold}
              </span>
            )}
            {event.rewards_stardust < 0 && (
              <span className="reward-item reward-negative">
                <img
                  src="/data/interface/stardust.webp"
                  className="reward-icon"
                  alt="stardust"
                />{' '}
                {event.rewards_stardust}
              </span>
            )}
          </>
        )}
      </div>
    );
  }

  if (c.isFriendlyMeet) {
    return (
      <div className="journal-entry-rewards">
        <span className="reward-item reward-positive">
          💚 полное восстановление
        </span>
        <span className="reward-item reward-positive">💕 +3</span>
      </div>
    );
  }

  if (!success && !c.isSystemEvent) {
    return (
      <div className="journal-entry-rewards">
        <span className="reward-item reward-negative">⚡ -30</span>
      </div>
    );
  }

  return null;
}

function DungeonLabel({
  event,
  c,
}: {
  event: GameEvent;
  c: ReturnType<typeof classifyEvent>;
}) {
  if (c.isDungeonBoss) {
    return <span className="journal-dungeon-tag boss">Босс</span>;
  }
  if (c.isDungeon) {
    return (
      <>
        <span className="journal-dungeon-tag">Подземелье</span>
        {event.dungeon_stage && event.dungeon_total && (
          <span className="journal-dungeon-stage">
            Этап {event.dungeon_stage}/{event.dungeon_total}
          </span>
        )}
      </>
    );
  }
  return null;
}

export function EventItem({ event }: { event: GameEvent }) {
  // PostgreSQL returns true/false, SQLite returns 1/0
  const success = (event.success as unknown as boolean | number) === true ||
    (event.success as unknown as number) === 1;
  const c = classifyEvent(event);
  const entryClass = getEntryClass(c);
  const resultText = getResultText(event, success, c);
  const resultClass = success ? 'success' : 'failure';
  const timeStr = formatTime(event.created_at);

  return (
    <div className={`journal-entry ${entryClass}`}>
      <div className="journal-entry-header">
        <span className="journal-entry-location">
          {event.location_name || 'Неизвестная локация'}
          <DungeonLabel event={event} c={c} />
        </span>
        <span className={`journal-entry-result ${resultClass}`}>{resultText}</span>
      </div>
      {event.description && (
        <div className="journal-entry-description">{event.description}</div>
      )}
      {event.result_text && (
        <div className="journal-entry-outcome">{event.result_text}</div>
      )}
      <div className="journal-entry-footer">
        <RewardsBlock event={event} success={success} c={c} />
        <div className="journal-entry-time">{timeStr}</div>
      </div>
    </div>
  );
}
